import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { COMPANY } from "../data/insurances";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.email.includes("@") &&
    form.message.trim().length >= 10;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensagem enviada! Retornaremos em breve.");
      setForm({ name: "", email: "", whatsapp: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-16 md:py-24 bg-surface min-h-[80vh]" data-testid="contact-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center mb-14">
          <p className="text-lego text-sm font-bold uppercase tracking-widest mb-3">Fale com a gente</p>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-navy">
            Estamos aqui para ouvir.
          </h1>
          <p className="mt-4 text-navy/70 max-w-xl mx-auto">
            Tire dúvidas, faça sugestões ou apenas diga oi. Respondemos sempre.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info side */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-navy text-white rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute inset-0 brick-pattern opacity-20" />
              <div className="relative">
                <h2 className="font-heading text-2xl font-bold mb-6">Safe Life</h2>
                <ul className="space-y-5 text-sm">
                  <li className="flex gap-3">
                    <i className="fa-solid fa-location-dot text-lego mt-1" />
                    <div>
                      <p className="font-bold mb-1">Endereço</p>
                      <p className="text-white/80">{COMPANY.address}<br />CEP {COMPANY.zipCode}</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <i className="fa-brands fa-whatsapp text-lego mt-1" />
                    <div>
                      <p className="font-bold mb-1">WhatsApp</p>
                      <a href={`https://wa.me/55${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className="text-white/80 hover:text-lego">
                        {COMPANY.whatsappDisplay}
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <i className="fa-solid fa-envelope text-lego mt-1" />
                    <div>
                      <p className="font-bold mb-1">E-mail</p>
                      <a href={`mailto:${COMPANY.email}`} className="text-white/80 hover:text-lego break-all">
                        {COMPANY.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <i className="fa-solid fa-clock text-lego mt-1" />
                    <div>
                      <p className="font-bold mb-1">Horário</p>
                      <p className="text-white/80">Seg–Sex: 8h–18h<br />Sáb: 9h–13h</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 border border-navy-100">
              <p className="text-xs text-navy/60 font-semibold uppercase tracking-wider mb-2">Dados legais</p>
              <p className="text-sm text-navy/80">CNPJ: {COMPANY.cnpj}</p>
              <p className="text-sm text-navy/80">SUSEP: {COMPANY.susep}</p>
            </div>
          </div>

          {/* Form side */}
          <form onSubmit={onSubmit} className="lg:col-span-3 bg-white rounded-3xl p-8 md:p-10 border border-navy-100 shadow-soft space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="contact-name"
                />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  placeholder="(11) 99999-9999"
                  data-testid="contact-whatsapp"
                />
              </div>
              <div>
                <Label>Assunto</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Dúvida sobre seguro auto"
                  data-testid="contact-subject"
                />
              </div>
            </div>
            <div>
              <Label>Mensagem *</Label>
              <Textarea
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                placeholder="Como podemos ajudar?"
                data-testid="contact-message"
              />
            </div>

            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full bg-brand hover:bg-brand-600 active:bg-brand-700 text-white rounded-full h-14 font-bold disabled:opacity-40"
              data-testid="contact-submit-btn"
            >
              {submitting ? (
                <><i className="fa-solid fa-spinner fa-spin mr-2" /> Enviando...</>
              ) : (
                <><i className="fa-solid fa-paper-plane mr-2" /> Enviar mensagem</>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
